import React, { useContext, useMemo } from 'react';
import {
    DetailDossierTitle,
    DetailDossierToolbar,
    SelectedDossierContext,
} from '@europrocurement/l2d-modules';
import {
    PRESCRIPTEUR_SLICE_NAME,
    selectedOffer,
    selectedPrescripteursSelector,
    selectedSousClientSelector,
    SOUS_CLIENT_SLICE_NAME,
    type TiersAppDispatch,
    useOffreService,
    useTiersService,
} from '@europrocurement/l2d-domain';
import { useDispatch, useSelector } from 'react-redux';
import {
    HeaderSkeleton,
    SkeletonLoader,
    useProgressiveSkeleton,
} from '@europrocurement/flexy-components';
import { prescripteursBasePath } from '../../../Prescripteurs/constants/paths';

/**
 * Composant header du détail d'un dossier.
 * Gère le chargement progressif des données (prescripteur, offre, client opéré)
 * et affiche un skeleton durant le chargement.
 */
const DetailDossierHeader: React.FunctionComponent = function () {
    const { dossier, siblings, refreshDossier, updateDossier } = useContext(SelectedDossierContext);
    const { selectSousClients, selectPrescripteur } = useTiersService();
    const { selectOffer } = useOffreService();
    const dispatch = useDispatch<TiersAppDispatch>();

    const [currentDossier, setCurrentDossier] = React.useState(dossier);
    const [currentClientOpereId, setCurrentClientOpereId] = React.useState(dossier.clientOpereId);

    const prescripteur = useSelector(selectedPrescripteursSelector);
    const offre = useSelector(selectedOffer);
    const clientOpere = useSelector(selectedSousClientSelector);

    React.useEffect(() => {
        if (dossier.id !== currentDossier.id) setCurrentDossier(dossier);
    }, [dossier, currentDossier]);

    React.useEffect(() => {
        if (dossier.clientOpereId !== currentClientOpereId) {
            setCurrentClientOpereId(dossier.clientOpereId);
        }
    }, [currentClientOpereId, dossier.clientOpereId]);

    /**
     * Hook de gestion du chargement progressif.
     * Permet de suivre l'état de chargement de chaque étape.
     */
    const { completeStep, progress } = useProgressiveSkeleton({
        steps: ['prescripteur', 'offre', 'sousClient'],
    });

    /**
     * Chargement des données du prescripteur.
     * Complete l'étape même si l'ID est manquant ou en cas d'erreur.
     */
    React.useEffect(() => {
        if (!currentDossier.prescripteurId) {
            completeStep('prescripteur');
            return;
        }

        dispatch(selectPrescripteur({ id: currentDossier.prescripteurId.toString() }))
            .unwrap()
            .then(() => completeStep('prescripteur'))
            .catch(() => completeStep('prescripteur'));
    }, [dispatch, currentDossier, selectPrescripteur, completeStep]);

    /**
     * Chargement des données de l'offre.
     * Complete l'étape même si l'ID est manquant ou en cas d'erreur.
     */
    React.useEffect(() => {
        if (!currentDossier.offreId) {
            completeStep('offre');
            return;
        }

        dispatch(selectOffer({ id: currentDossier.offreId }))
            .unwrap()
            .then(() => completeStep('offre'))
            .catch(() => completeStep('offre'));
    }, [dispatch, currentDossier, selectOffer, completeStep]);

    /**
     * Chargement des données du client opéré.
     * Complete l'étape même si l'ID est manquant ou en cas d'erreur.
     */
    React.useEffect(() => {
        if (!currentClientOpereId) {
            completeStep('sousClient');
        } else {
            dispatch(selectSousClients({ id: currentClientOpereId }))
                .unwrap()
                .then(() => completeStep('sousClient'));
        }

        return () => {
            dispatch({
                type: `${SOUS_CLIENT_SLICE_NAME}/deletemainSelected`,
            });
            dispatch({
                type: `${PRESCRIPTEUR_SLICE_NAME}/deletemainSelected`,
            });
        };
    }, [completeStep, currentClientOpereId, dispatch, selectSousClients]);

    // Marquer le dossier comme prêt (toujours instantané car déjà disponible)
    React.useEffect(() => {
        completeStep('dossierReady');
    }, [completeStep]);

    // URLs et données formatées
    const clientOpereSocieteDotComUrl = useMemo(() => {
        if (!clientOpere?.siren) return undefined;
        return `https://www.societe.com/cgi-bin/fiche?rncs=${clientOpere.siren}`;
    }, [clientOpere?.siren]);

    /** URL vers la page de détail du prescripteur */
    const prescripteurDetailUrl = useMemo(() => {
        if (!prescripteursBasePath || !prescripteur?.id) return undefined;
        return `/${prescripteursBasePath}/liste/${prescripteur.id}`;
    }, [prescripteur?.id]);

    /** Données du dossier formatées pour l'affichage */
    const formattedDossier = useMemo(
        () => ({
            statut: currentDossier.statutLibelle as string,
            societeRef: currentDossier.societeRef as string,
            statutId: currentDossier.statutId as number,
        }),
        [currentDossier.statutLibelle, currentDossier.societeRef, currentDossier.statutId],
    );

    /** Données du prescripteur formatées pour l'affichage */
    const formattedPrescripteur = useMemo(
        () => ({
            id: prescripteur?.id as number,
            raisonSociale: (prescripteur?.nomDentreprise as string) || 'Pas de raison sociale',
            codeComptable:
                prescripteur?.relationsSocietes[0]?.codeClient || 'Pas de code comptable',
            urlToDetails: prescripteurDetailUrl,
        }),
        [prescripteur, prescripteurDetailUrl],
    );

    /** Données de l'offre formatées pour l'affichage */
    const formattedOffre = useMemo(
        () => ({
            libelle: offre?.libelle || 'Aucune offre',
        }),
        [offre?.libelle],
    );

    /** Données du client opéré formatées pour l'affichage */
    const formattedClientOpere = useMemo(
        () => ({
            raisonSociale: clientOpere?.raisonSociale || 'Pas de raison sociale',
            siren: clientOpere?.siren,
            urlToSocieteDotCom: clientOpereSocieteDotComUrl,
        }),
        [clientOpere?.raisonSociale, clientOpere?.siren, clientOpereSocieteDotComUrl],
    );

    const isMultiDossiers = siblings.length > 1;
    const showTitleSkeleton = progress < 100;

    return (
        <>
            {/* Toolbar affichée dès que dossier et siblings sont disponibles */}
            <DetailDossierToolbar
                dossier={currentDossier}
                source="BACK"
                siblingDossiers={siblings}
                isMultiDossiers={isMultiDossiers}
            />

            {/* Title affiché seulement quand toutes les données sont prêtes */}
            <SkeletonLoader
                isLoading={showTitleSkeleton}
                skeleton={<HeaderSkeleton />}
            >
                <DetailDossierTitle
                    dossier={formattedDossier}
                    prescripteur={formattedPrescripteur}
                    offre={formattedOffre}
                    clientOpere={formattedClientOpere}
                    color="primary"
                    refreshDossier={refreshDossier}
                    updateDossier={updateDossier}
                    icon={isMultiDossiers ? 'folders' : 'folder'}
                />
            </SkeletonLoader>
        </>
    );
};

export default DetailDossierHeader;
